import { IGetPaymentsHistoryResponse } from 'services/payments/payments/types';

export const GA_MEASUREMENT_ID = 'G-0Q485KXFGT';

type GtagFunction = (...args: unknown[]) => void;

interface IGtagWindow extends Window {
  dataLayer?: unknown[];
  gtag?: GtagFunction;
}

const getGtagWindow = (): IGtagWindow => window as IGtagWindow;

let gtagInitPromise: Promise<void> | null = null;

/** Initializes Google Analytics gtag.js (mirrors the standard GA snippet). */
export const ensureGtagInitialized = (): Promise<void> => {
  if (gtagInitPromise) {
    return gtagInitPromise;
  }

  gtagInitPromise = new Promise((resolve) => {
    const gtagWindow = getGtagWindow();
    gtagWindow.dataLayer = gtagWindow.dataLayer || [];

    if (!gtagWindow.gtag) {
      gtagWindow.gtag = (...args: unknown[]) => {
        gtagWindow.dataLayer!.push(args);
      };
    }

    gtagWindow.gtag('js', new Date());
    gtagWindow.gtag('config', GA_MEASUREMENT_ID);

    const existingScript = document.querySelector(`script[src*="gtag/js?id=${GA_MEASUREMENT_ID}"]`);
    if (existingScript) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });

  return gtagInitPromise;
};

const getPurchaseItemVariant = (billingPeriod: string | undefined): string => {
  if (billingPeriod === 'Annually') {
    return 'Yearly';
  }

  return 'Monthly';
};

export const trackPurchaseWithGtag = (paymentData: IGetPaymentsHistoryResponse): boolean => {
  const gtagWindow = getGtagWindow();

  if (!paymentData.content.length || typeof gtagWindow.gtag !== 'function') {
    return false;
  }

  const latestPayment = paymentData.content[0];
  const operationId = latestPayment.operationId;
  const storageKey = `ga_purchase_tracked_${operationId}`;

  if (sessionStorage.getItem(storageKey)) {
    return true;
  }

  const subscriptionNameParts = latestPayment.order.subscription.name.split(' ');
  const itemName = subscriptionNameParts[0];
  const billingPeriod = subscriptionNameParts[1];
  const currency = latestPayment.order.subscription.currency.toUpperCase();
  const price = latestPayment.order.subscription.price / 100;

  gtagWindow.gtag('event', 'purchase', {
    transaction_id: operationId,
    value: price,
    tax: 0,
    shipping: 0,
    currency,
    items: [
      {
        item_name: itemName,
        item_variant: getPurchaseItemVariant(billingPeriod),
        price,
        quantity: 1,
      },
    ],
  });

  sessionStorage.setItem(storageKey, '1');
  return true;
};
